import { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../context/authContext.jsx';
import { initiateSocketConnection } from '../service/socketio.js';

// Roles que reciben/resuelven alertas de Mejoramientos (migración 076).
// 'admin' NO está: entra a la página desde su menú Admin y no necesita la
// burbuja de pendientes flotante (la usan los roles del Área de Mejoramientos).
export const ROLES_ALERTA = ['mejoramientos', 'admin_mejoramientos'];

// Recupera las alertas vencidas del Área de Mejoramientos para el usuario
// logueado, con el mismo patrón que las notificaciones pendientes: se carga al
// montar (al loguear) y después con un poll silencioso, así el rol
// 'mejoramientos' las ve sin tener que refrescar.
//
// devuelve: { pendientes, cargando, error, puede, recargar, resolver }
export const useAlertasMejoramientos = ({ intervaloMs = 60000 } = {}) => {
    const { token, role, roles } = useContext(AuthContext);

    const [pendientes, setPendientes] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');

    // Multi-rol (074): alcanza con que alguno de los roles del usuario matchee.
    // Los tokens viejos hidratan roles=[role], así que el fallback es idéntico.
    const puede = useMemo(() => {
        if (!role) return false;
        if (Array.isArray(roles) && roles.some((r) => ROLES_ALERTA.includes(r))) return true;
        return ROLES_ALERTA.includes(role);
    }, [role, roles]);

    const recargar = useCallback(async () => {
        if (!puede || !token) return [];
        setCargando(true);
        try {
            const res = await fetch('/api/alertas-mejoramientos/pendientes', {
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store',
            });
            if (!res.ok) {
                setError('No se pudieron cargar las alertas');
                setPendientes([]);
                return [];
            }
            const data = await res.json();
            const ventanas = data?.data || [];
            setPendientes(ventanas);
            setError('');
            return ventanas;
        } catch (e) {
            console.error('[alertas mejoramientos] error:', e);
            setError(e.message);
            return [];
        } finally {
            setCargando(false);
        }
    }, [puede, token]);

    // Carga inicial (al loguear / montar la sesión) + poll silencioso.
    useEffect(() => {
        recargar();
    }, [recargar]);

    useEffect(() => {
        if (!puede) return undefined;
        const id = setInterval(recargar, intervaloMs);
        return () => clearInterval(id);
    }, [puede, recargar, intervaloMs]);

    // Tiempo real: cuando OTRO usuario del Área resuelve una alerta, el backend emite
    // 'alertas_mejoramientos_actualizadas' y recargamos el contador al toque, sin
    // esperar el poll. El emisor ya recarga solo (en resolver()); acá cubrimos al
    // resto, que es lo que no tenía forma de enterarse.
    useEffect(() => {
        if (!puede || !token) return undefined;
        const s = initiateSocketConnection();
        const onActualizada = () => { recargar(); };
        s.on('alertas_mejoramientos_actualizadas', onActualizada);
        return () => { s.off('alertas_mejoramientos_actualizadas', onActualizada); };
    }, [puede, token, recargar]);

    // Cierra la alerta con el NRO PV de la Providencia (obligatorio). La
    // notificación es una acción aparte y NO resuelve el ciclo. La descripción es
    // opcional. Devuelve { ok, data, error }.
    const resolver = useCallback(async (id, nroPv, descripcion, plazo, email) => {
        if (!token) return { ok: false, error: 'No autenticado' };
        const nroPvLimpio = (nroPv ?? '').trim();
        const descripcionLimpia = (descripcion ?? '').trim();
        if (!nroPvLimpio) {
            return { ok: false, error: 'Cargá el NRO PV de la Providencia' };
        }
        try {
            const res = await fetch(`/api/alertas-mejoramientos/${id}/resolver`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    nro_pv: nroPvLimpio,
                    descripcion: descripcionLimpia || undefined,
                    plazo: plazo ?? undefined,
                    email: email ?? undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return { ok: false, error: data?.message || 'Error al resolver la alerta' };
            }
            await recargar();
            return { ok: true, data: data?.data };
        } catch (e) {
            console.error('[alertas mejoramientos] resolver error:', e);
            return { ok: false, error: e.message };
        }
    }, [token, recargar]);

    // Trae la plantilla editable del mail de notificación (destinatarios, asunto
    // y cuerpo). Lo usa el modal antes de enviar.
    const prepararNotificacion = useCallback(async (id) => {
        if (!token) return { ok: false, error: 'No autenticado' };
        try {
            const res = await fetch(`/api/alertas-mejoramientos/${id}/notificacion`, {
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return { ok: false, error: data?.message || 'No se pudo preparar la notificación' };
            }
            return { ok: true, data: data?.data };
        } catch (e) {
            console.error('[alertas mejoramientos] preparar notificación error:', e);
            return { ok: false, error: e.message };
        }
    }, [token]);

    // Trae la plantilla editable del mail de RESPUESTA del ciclo 1, armada para
    // el plazo elegido (incluye la nueva fecha límite). Se edita y se guarda
    // como borrador; recién después se habilita resolver.
    const prepararEmailRespuesta = useCallback(async (id, plazo) => {
        if (!token) return { ok: false, error: 'No autenticado' };
        if (!plazo) return { ok: false, error: 'Elegí primero el plazo otorgado' };
        try {
            const res = await fetch(
                `/api/alertas-mejoramientos/${id}/email-respuesta?plazo=${encodeURIComponent(plazo)}`,
                { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return { ok: false, error: data?.message || 'No se pudo preparar el mail de respuesta' };
            }
            return { ok: true, data: data?.data };
        } catch (e) {
            console.error('[alertas mejoramientos] preparar mail de respuesta error:', e);
            return { ok: false, error: e.message };
        }
    }, [token]);

    // Envía el mail con el texto editado. No cierra el ciclo: la alerta sigue
    // pendiente. El envío queda registrado en Seguimiento de Intercambios (sin
    // PV, con la descripción opcional). Devuelve { ok, data, error }.
    const enviarNotificacion = useCallback(async (id, { destinatarios, asunto, cuerpo, descripcion }) => {
        if (!token) return { ok: false, error: 'No autenticado' };
        if (!(asunto ?? '').trim()) return { ok: false, error: 'El asunto es obligatorio' };
        if (!(cuerpo ?? '').trim()) return { ok: false, error: 'El cuerpo del mail es obligatorio' };
        try {
            const res = await fetch(`/api/alertas-mejoramientos/${id}/notificacion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    destinatarios: (destinatarios ?? '').trim() || undefined,
                    asunto: asunto.trim(),
                    cuerpo: cuerpo.trim(),
                    descripcion: (descripcion ?? '').trim() || undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                return { ok: false, error: data?.message || 'No se pudo enviar la notificación' };
            }
            await recargar();
            return { ok: true, data: data?.data };
        } catch (e) {
            console.error('[alertas mejoramientos] enviar notificación error:', e);
            return { ok: false, error: e.message };
        }
    }, [token, recargar]);

    return { pendientes, cargando, error, puede, recargar, resolver, prepararNotificacion, prepararEmailRespuesta, enviarNotificacion };
};